// Super-admin tenant browse (GET /restaurants?query= — newest first with no
// query, name match otherwise) + provisioning.
//
// Provisioning is a guided three-step wizard (brand → first store → owner)
// over three endpoints — POST /restaurants, POST …/stores, POST …/users —
// each step commits on its way out, so a mid-flow failure resumes where the
// last commit left off. The owner step is skippable: a QA test store
// usually has no owner.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { storefrontUrl } from '../../lib/links';
import { AU_TIMEZONES, type Restaurant, type Store } from '../restaurant/types';

/** The store form's "same as the brand" choice — sent as the brand's zone,
 * because the server's own default is a constant, not the brand's. */
const INHERIT = 'inherit';

type Wizard = {
  step: 1 | 2 | 3 | 'done';
  // step 1 commits these
  rid?: string;
  restaurantName?: string;
  // step 2 commits this
  sid?: string;
};

function Steps(props: { step: 1 | 2 | 3 }) {
  const labels = ['Brand', 'First store', 'Owner'];
  return (
    <div className="steps">
      {labels.map((label, i) => {
        const n = i + 1;
        const cls = n < props.step ? 'done' : n === props.step ? 'on' : '';
        return (
          <div key={label} className={`step ${cls}`}>
            {n < props.step && <span className="tick">✓</span>}
            {n} · {label}
          </div>
        );
      })}
    </div>
  );
}

export function Restaurants() {
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<Restaurant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [wizard, setWizard] = useState<Wizard | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [brand, setBrand] = useState({ name: '', timezone: 'Australia/Sydney' });
  const [store, setStore] = useState({ name: '', address: '', phoneE164: '', timezone: INHERIT });
  const [owner, setOwner] = useState({ email: '', role: 'admin' });

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api.get<Restaurant[]>('/restaurants', { query: query.trim() || undefined })
        .then((r) => { if (live) { setRows(r); setError(null); } })
        .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load.'); });
    }, query ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [query, reload]);

  function openWizard() {
    setBrand({ name: '', timezone: 'Australia/Sydney' });
    setStore({ name: '', address: '', phoneE164: '', timezone: INHERIT });
    setOwner({ email: '', role: 'admin' });
    setFormError(null);
    setWizard({ step: 1 });
  }

  async function commit(fn: () => Promise<void>) {
    setSaving(true);
    setFormError(null);
    try {
      await fn();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setSaving(false);
    }
  }

  const createRestaurant = () => commit(async () => {
    const r = await api.post<Restaurant>('/restaurants', {
      name: brand.name.trim(), timezone: brand.timezone, country: 'AU',
    });
    setReload((n) => n + 1);
    setWizard({ step: 2, rid: r.restaurantId, restaurantName: r.name });
  });

  const createStore = () => commit(async () => {
    if (!wizard?.rid) return;
    const s = await api.post<Store>(`/restaurants/${wizard.rid}/stores`, {
      name: store.name.trim(),
      address: store.address.trim(),
      phoneE164: store.phoneE164.trim(),
      timezone: store.timezone === INHERIT ? brand.timezone : store.timezone,
    });
    setWizard({ ...wizard, step: 3, sid: s.storeId });
  });

  const addOwner = () => commit(async () => {
    if (!wizard?.rid) return;
    await api.post(`/restaurants/${wizard.rid}/users`, {
      email: owner.email.trim(), role: owner.role,
    });
    setWizard({ ...wizard, step: 'done' });
  });

  const w = wizard;
  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform</div>
          <h1>Restaurants</h1>
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={openWizard}>
          <Plus size={14} /> Provision restaurant
        </button>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div className="filters" style={{ marginBottom: 16 }}>
        <input className="input" aria-label="Search restaurants" placeholder="Search restaurants…" value={query}
          style={{ maxWidth: 320 }} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {rows === null && !error && <div className="loading">Loading…</div>}
      {rows?.length === 0 && <div className="loading">No restaurants match.</div>}
      {rows && rows.length > 0 && (
        <table className="tbl" data-section="restaurants">
          <thead>
            <tr>
              <th>Name</th>
              <th>Id</th>
              <th>Timezone</th>
              <th>Country</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.restaurantId} data-restaurant={r.restaurantId} onClick={() => nav(`/r/${r.restaurantId}`)}>
                <td style={{ fontWeight: 700 }}>{r.name}</td>
                <td className="docket" style={{ fontWeight: 500 }}>{r.restaurantId}</td>
                <td style={{ color: 'var(--ink-2)' }}>{r.timezone}</td>
                <td style={{ color: 'var(--ink-2)' }}>{r.country}</td>
                <td style={{ color: 'var(--ink-3)' }}>
                  {new Date(r.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {w && (
        <div className="scrim">
          <div className="modal" role="dialog" aria-label="Provision a restaurant" data-section="provision">
            {w.step !== 'done' && (
              <div className="modal-head">
                <div className="overline">Provision</div>
                <h2>New restaurant &amp; first store</h2>
                <Steps step={w.step} />
              </div>
            )}

            {w.step === 1 && (
              <>
                <div className="modal-body">
                  {formError && <div className="err" role="alert">{formError}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="field">
                      <label htmlFor="prov-name">Restaurant name</label>
                      <input id="prov-name" className="input" value={brand.name} autoFocus
                        onChange={(e) => setBrand({ ...brand, name: e.target.value })} />
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                        QA convention: prefix test tenants with “QA —” so they sort together.
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div className="field">
                        <label htmlFor="prov-tz">Timezone</label>
                        <select id="prov-tz" className="input" value={brand.timezone}
                          onChange={(e) => setBrand({ ...brand, timezone: e.target.value })}>
                          {AU_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </div>
                      <div className="field">
                        <label>Country</label>
                        <select className="input" value="AU" disabled>
                          <option value="AU">Australia (AU)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="btn btn-ghost btn-sm" onClick={() => setWizard(null)}>Cancel</button>
                  <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
                    disabled={saving || !brand.name.trim()} onClick={() => void createRestaurant()}>
                    {saving ? 'Creating…' : 'Create restaurant →'}
                  </button>
                </div>
              </>
            )}

            {w.step === 2 && (
              <>
                <div className="modal-body">
                  <div className="committed">
                    <span className="tick">✓</span> Restaurant created — <code data-rid={w.rid}>{w.rid}</code>
                  </div>
                  {formError && <div className="err" role="alert">{formError}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="field">
                      <label htmlFor="prov-store-name">Store name</label>
                      <input id="prov-store-name" className="input" value={store.name} autoFocus
                        onChange={(e) => setStore({ ...store, name: e.target.value })} />
                    </div>
                    <div className="field">
                      <label htmlFor="prov-store-address">Address</label>
                      <input id="prov-store-address" className="input" value={store.address}
                        onChange={(e) => setStore({ ...store, address: e.target.value })} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div className="field">
                        <label htmlFor="prov-store-phone">Phone (E.164)</label>
                        <input id="prov-store-phone" className="input" placeholder="+61240000000" value={store.phoneE164}
                          onChange={(e) => setStore({ ...store, phoneE164: e.target.value })} />
                      </div>
                      <div className="field">
                        <label htmlFor="prov-store-tz">Store timezone</label>
                        <select id="prov-store-tz" className="input" value={store.timezone}
                          onChange={(e) => setStore({ ...store, timezone: e.target.value })}>
                          <option value={INHERIT}>Inherit — {brand.timezone}</option>
                          {AU_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </div>
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      Creates the store and its sync position in one transaction; pickup is on, the other channels off.
                    </span>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="btn btn-ghost btn-sm" onClick={() => setWizard(null)}>Finish later</button>
                  <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
                    disabled={saving || !store.name.trim() || !store.address.trim() || !store.phoneE164.trim()}
                    onClick={() => void createStore()}>
                    {saving ? 'Creating…' : 'Create store →'}
                  </button>
                </div>
              </>
            )}

            {w.step === 3 && (
              <>
                <div className="modal-body">
                  <div className="committed">
                    <span className="tick">✓</span> Store created — <code data-sid={w.sid}>{w.sid}</code>
                  </div>
                  {formError && <div className="err" role="alert">{formError}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="field">
                      <label htmlFor="prov-owner">Owner email</label>
                      <input id="prov-owner" className="input" type="email" placeholder="owner@example.com.au" value={owner.email} autoFocus
                        onChange={(e) => setOwner({ ...owner, email: e.target.value })} />
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                        They get the login email and appear under Members — no prior sign-in needed.
                      </span>
                    </div>
                    <div className="field" style={{ maxWidth: 200 }}>
                      <label htmlFor="prov-owner-role">Role</label>
                      <select id="prov-owner-role" className="input" value={owner.role}
                        onChange={(e) => setOwner({ ...owner, role: e.target.value })}>
                        <option value="admin">admin</option>
                        <option value="user">user</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="btn btn-outline btn-sm" disabled={saving}
                    onClick={() => setWizard({ ...w, step: 'done' })}>
                    Skip for now
                  </button>
                  <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
                    disabled={saving || !owner.email.trim()} onClick={() => void addOwner()}>
                    {saving ? 'Adding…' : 'Add owner & finish →'}
                  </button>
                </div>
              </>
            )}

            {w.step === 'done' && (
              <>
                <div className="modal-body">
                  <div className="wizard-done">
                    <div className="badge-ok"><Check size={26} strokeWidth={3} /></div>
                    <h3>{w.restaurantName} is live</h3>
                    <p className="p">
                      The restaurant and its first store are provisioned.
                      {!owner.email.trim() && ' No owner yet — add one any time from Members.'}
                    </p>
                    <div className="done-card">
                      <div className="dr"><span className="k">Restaurant</span><span className="v">{w.rid}</span></div>
                      <div className="dr"><span className="k">Store</span><span className="v">{w.sid}</span></div>
                      <div className="dr">
                        <span className="k">Storefront</span>
                        <a className="v" href={storefrontUrl(w.rid ?? '', w.sid ?? '')} target="_blank" rel="noreferrer">
                          {storefrontUrl(w.rid ?? '', w.sid ?? '')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="btn btn-ghost btn-sm" onClick={openWizard}>Provision another</button>
                  <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setWizard(null)}>
                    Close
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => nav(`/r/${w.rid}/s/${w.sid}`)}>
                    Open store admin →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
